import React from "react";
import { EmptyState, Icon, Portal, CompanyLogo, PublicCustomerHub, notify, confirmDialog, ListRow, useSupportAdmin } from "./registry";
/* ============================================================
   Fence Works — Integrations, Customer Hub, Estimator Widget
   ============================================================ */

/* ---- integration catalog (connect/disconnect framework) ----
   ONLY tools the CONTRACTOR owns an account for live here. Platform plumbing
   (Resend email, Retell voice, Twilio SMS, our own CRM/lead API) is configured
   by Fence Works server-side — contractors never see or connect it, so those
   cards are gone from this catalog on purpose.
   `dev` marks integrations whose connect handshake is real (OAuth / key →
   Vault) but whose data sync isn't live yet — the card says so honestly. */
const INTEGRATIONS: any[] = [
  { id: "stripe", name: "Stripe", cat: "Payments", color: "#635bff", icon: "dollar",
    blurb: "Collect deposits & balances by card and ACH right from the quote and the customer hub — straight into YOUR Stripe account. Already use Stripe? Sign in and pick your account. New to Stripe? Create one in a few minutes. Refunds, disputes and payouts stay in your own Stripe dashboard.",
    fields: [],
    scopes: ["Charge deposits & balances", "Customer-hub pay buttons", "Paid status, refunds & disputes flow back automatically"] },
  { id: "wisetack", name: "Wisetack", cat: "Financing", color: "#1a7f5a", icon: "dollar",
    blurb: "Offer real monthly financing on quotes from YOUR Wisetack account. Setup lives on the dedicated Financing page — get a Wisetack account, connect it there, and Fence Works pipes the rest.",
    fields: [],
    scopes: ["Generate prequal links", "Track funded loans", "Quotes flagged financed on approval"] },
  { id: "gcal", name: "Google Calendar", cat: "Scheduling", color: "#4285f4", icon: "calendar",
    blurb: "One click: sign in with Google, grant calendar access, and every job you schedule lands on your Google Calendar automatically — customer, address, crew & fence details. Reschedule updates the event; clear schedule removes it.",
    fields: [{ key: "calendarId", label: "Calendar ID (optional — after connecting, under Manage)", ph: "blank = your main calendar" }],
    scopes: ["Scheduled jobs → calendar events", "Reschedule updates the event", "Clear schedule removes it"] },
  { id: "quickbooks", name: "QuickBooks", cat: "Accounting", color: "#2ca01c", icon: "clipboard",
    blurb: "Push an estimate straight into QuickBooks Online — it creates the customer and a sales invoice mapped to your income account. Hit \"Send to QuickBooks\" on any quote.",
    dev: "Heads up on sales tax: it now posts as proper sales tax via the invoice's tax detail (a tax-liability account), not as a line item. If your QuickBooks uses Automated Sales Tax, QuickBooks may recalculate tax from the customer's address and override the amount — verify the tax on your first synced invoice matches your quote.",
    fields: [{ key: "realmId", label: "Company (realm) ID", ph: "auto-filled on connect" }],
    scopes: ["Create invoices & customers", "Sales tax → tax-liability account", "Two-way payment sync"] },
  { id: "jobber", name: "Jobber", cat: "Field Ops", color: "#1f9b4e", icon: "box",
    blurb: "Hand off won jobs to Jobber for dispatch, time tracking, and invoicing if your crews already live there.",
    dev: "Stores your key securely today; the job handoff is in development — nothing is pulled or pushed yet.",
    fields: [{ key: "apiKey", label: "API key", ph: "jb_live_…" }],
    scopes: ["Create jobs & clients", "Sync job status", "Push line items"] },
  { id: "supplier", name: "Suppliers / Distributors", cat: "Procurement", color: "#f0a35e", icon: "box",
    blurb: "Save every yard you buy from — order email, account #, your rep. A paid job's bill of materials becomes a one-click order emailed straight to the right rep.",
    dev: "Saves your suppliers today; one-click ordering is in development (supplier catalogs first).",
    fields: [],
    scopes: ["As many suppliers as you use", "Itemized PO from the estimate", "One-click material orders"] },
  { id: "gsheets", name: "Google Sheets", cat: "Lead Routing", color: "#0f9d58", icon: "clipboard",
    blurb: "Every captured lead appended as a row to your Google Sheet — Date, Name, Phone, Email, Address, Estimate $, Source. Pick the destination sheet in AI Front Desk → Settings → Lead routing.",
    fields: [],
    scopes: ["Append each new lead as a row", "Auto header row on an empty sheet", "Sheet errors never block lead capture"] },
  { id: "gmail", name: "Gmail — send as you", cat: "Email Sending", color: "#ea4335", icon: "mail",
    blurb: "Review & referral sequences and your quote emails send straight from YOUR Gmail / Google Workspace address — they sit in your Sent folder and replies land in your inbox. Appointment reminders & notifications still come via Fence Works for deliverability.",
    fields: [],
    scopes: ["Sequences & quote emails from your address", "Lives in your Sent folder, replies to your inbox", "Automatic fallback to Fence Works sending"] },
  { id: "m365", name: "Microsoft 365 — send as you", cat: "Email Sending", color: "#0078d4", icon: "mail",
    // No Azure app registration exists for this yet, so MS_CLIENT_ID and
    // MS_CLIENT_SECRET are unset and Connect can only ever dead-end. Marked the
    // same way Facebook is while it waits on review — the card stays honest and
    // the button stops promising something that cannot happen. Delete this line
    // the day the Azure app is registered and its two vars are in server/.env.
    soon: "Waiting on our Microsoft app registration — this switches on for everyone once it's done. Nothing for you to set up. Gmail works today, and quote emails send via Fence Works either way.",
    blurb: "Same as the Gmail option, for Outlook / Microsoft 365 mailboxes: sequences and quote emails send from your own address, with reminders & notifications still via Fence Works.",
    fields: [],
    scopes: ["Sequences & quote emails from your address", "Saved to your Sent Items", "Automatic fallback to Fence Works sending"] },
  { id: "gbp", name: "Google Business Profile", cat: "Reputation", color: "#4285f4", icon: "mail",
    blurb: "Connect your Google Business Profile and the AI auto-drafts a reply to each new review for one-tap approval — 3★ and under get flagged to you instead. New reviews are polled in the background. Turn on the social automations and finished jobs & top reviews can also go up as What's-New posts. Profile edits (description, website link, services) happen only when you press Apply on a specific suggestion, each one can be undone, and Fence Works never deletes anything on your profile.",
    fields: [],
    scopes: ["Read reviews, posts, photos & performance", "Post review replies you approve", "Publish What's-New posts", "Apply profile edits you approve (undoable)"] },
  { id: "facebook", name: "Facebook Page", cat: "Social", color: "#1877f2", icon: "send",
    // pending Meta's App Review for pages_manage_posts — flip `soon` off (delete
    // the line) the day it's approved and the live Connect flow takes over.
    soon: "Finishing review with Facebook — auto-posting switches on automatically once it's approved. Nothing to set up.",
    blurb: "Connect your business Page and the social automations handle the posting: a finished job goes up with its before & after photos and an AI-written caption, and 4–5★ Google reviews become shout-out posts. Every post waits for your one-tap approval unless you switch that off.",
    fields: [],
    scopes: ["Finished jobs posted with before & after photos", "Positive reviews become shout-out posts", "Approve each post — or let it run on full auto"] },
];

/* GBP location picker — one Google login can manage several businesses;
   the contractor picks which one Fence Works reads reviews from. Always shows
   its state (loading / error / empty / list) so a failure is never silent. */
function GBPLocationPicker() {
  const DB: any = (window as any).VFP_DB;
  const [st, setSt] = React.useState<any>({ s: "loading" });   // loading | ok | empty | error
  const [locs, setLocs] = React.useState<any[]>([]);
  const [cur, setCur] = React.useState<any>("");
  const [saved, setSaved] = React.useState<any>(false);
  const [busy, setBusy] = React.useState<any>(false);
  const load = React.useCallback(() => {
    setSaved(false); setSt({ s: "loading" });
    if (!DB || !DB.gbpLocations) { setSt({ s: "error", msg: "Needs the live service." }); return; }
    DB.gbpLocations().then((r: any) => {
      const list = r.locations || [];
      setLocs(list); setCur(r.current || (list[0] && list[0].parent) || "");
      setSt({ s: list.length ? "ok" : "empty" });
    }).catch((e: any) => setSt({ s: "error", msg: String((e && e.message) || e) }));
  }, []);
  React.useEffect(() => { load(); }, [load]);
  async function save() {
    if (!cur || !DB || !DB.gbpSetLocation) return;
    setBusy(true); setSaved(false);
    try { await DB.gbpSetLocation(cur); setSaved(true); } catch (e: any) { notify("Couldn't save the business: " + (e.message || e)); }
    finally { setBusy(false); }
  }
  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--line)" }}>
      <div className="row between" style={{ marginBottom: 6 }}>
        <label className="field-label" style={{ margin: 0 }}>Business to watch for reviews</label>
        <button className="btn btn-ghost btn-sm" style={{ padding: "2px 8px" }} onClick={load} title="Re-check"><Icon name="bolt" size={12} />Refresh</button>
      </div>
      {st.s === "loading" && <p className="faint" style={{ fontSize: 12, margin: 0 }}>Loading your businesses…</p>}
      {st.s === "ok" && (
        <div className="row gap-8" style={{ flexWrap: "wrap" }}>
          <select className="input" value={cur} style={{ flex: 1, minWidth: 200 }} onChange={e => { setCur(e.target.value); setSaved(false); }}>
            {locs.map((l: any) => <option key={l.parent} value={l.parent}>{l.title}{l.account ? " — " + l.account : ""}</option>)}
          </select>
          <button className="btn btn-outline btn-sm" disabled={busy || !cur} onClick={save}>{busy ? "Saving…" : saved ? "Saved ✓" : "Save"}</button>
        </div>
      )}
      {st.s === "empty" && <p className="muted" style={{ fontSize: 12, margin: 0 }}>No businesses found on the connected Google account. If your Business Profile lives on a different Google login, <b>Disconnect</b> above and reconnect with that account.</p>}
      {st.s === "error" && <p className="muted" style={{ fontSize: 12, margin: 0, color: "var(--amber)" }}>Couldn't load your businesses — {st.msg}. Most often that's because <b>Business Profile API access hasn't been granted yet</b> (request it in Google Cloud; it ships with quota 0 until approved). If the wrong Google account is connected, Disconnect above and reconnect.</p>}
    </div>
  );
}

/* Facebook page picker — one login can manage several Pages; the contractor
   picks which one the social automations post to. Same always-visible-state
   shape as the GBP picker above. */
function FacebookPagePicker() {
  const DB: any = (window as any).VFP_DB;
  const [st, setSt] = React.useState<any>({ s: "loading" });   // loading | ok | empty | error
  const [pages, setPages] = React.useState<any[]>([]);
  const [cur, setCur] = React.useState<any>("");
  const [saved, setSaved] = React.useState<any>(false);
  const [busy, setBusy] = React.useState<any>(false);
  const load = React.useCallback(() => {
    setSaved(false); setSt({ s: "loading" });
    if (!DB || !DB.facebookPages) { setSt({ s: "error", msg: "Needs the live service." }); return; }
    DB.facebookPages().then((r: any) => {
      const list = r.pages || [];
      setPages(list); setCur(r.current || (list[0] && list[0].id) || "");
      setSt({ s: list.length ? "ok" : "empty" });
    }).catch((e: any) => setSt({ s: "error", msg: String((e && e.message) || e) }));
  }, []);
  React.useEffect(() => { load(); }, [load]);
  async function save() {
    if (!cur || !DB || !DB.facebookSetPage) return;
    setBusy(true); setSaved(false);
    try { await DB.facebookSetPage(cur); setSaved(true); } catch (e: any) { notify("Couldn't save the page: " + (e.message || e)); }
    finally { setBusy(false); }
  }
  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--line)" }}>
      <div className="row between" style={{ marginBottom: 6 }}>
        <label className="field-label" style={{ margin: 0 }}>Page to post to</label>
        <button className="btn btn-ghost btn-sm" style={{ padding: "2px 8px" }} onClick={load} title="Re-check"><Icon name="bolt" size={12} />Refresh</button>
      </div>
      {st.s === "loading" && <p className="faint" style={{ fontSize: 12, margin: 0 }}>Loading your pages…</p>}
      {st.s === "ok" && (
        <div className="row gap-8" style={{ flexWrap: "wrap" }}>
          <select className="input" value={cur} style={{ flex: 1, minWidth: 200 }} onChange={e => { setCur(e.target.value); setSaved(false); }}>
            {pages.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button className="btn btn-outline btn-sm" disabled={busy || !cur} onClick={save}>{busy ? "Saving…" : saved ? "Saved ✓" : "Save"}</button>
        </div>
      )}
      {st.s === "empty" && <p className="muted" style={{ fontSize: 12, margin: 0 }}>No Pages on the connected Facebook login. Your business Page must be managed by this account — <b>Disconnect</b> above and reconnect with the right login.</p>}
      {st.s === "error" && <p className="muted" style={{ fontSize: 12, margin: 0, color: "var(--amber)" }}>Couldn't load your pages — {st.msg}. If the wrong Facebook account is connected, Disconnect above and reconnect.</p>}
    </div>
  );
}

function useIntegrations(store) {
  const conns = (store.settings && store.settings.integrations) || {};
  const setConn = (id, patch) => store.setSettings({ integrations: { ...conns, [id]: { ...(conns[id] || {}), ...patch } } });
  return [conns, setConn];
}

/* ---- suppliers: a LIST, not a single connection — most contractors buy from
   two or three yards. Lives in settings.suppliers; the supplier integration
   row just mirrors connected = (count > 0) for the chips/rails. ---- */
function SupplierManager({ store, conns, setConn }: any) {
  const list = Array.isArray(store.settings && store.settings.suppliers) ? store.settings.suppliers : [];
  const [editing, setEditing] = React.useState<any>(null);   // null | supplier draft
  function save(next: any[]) {
    store.setSettings({ suppliers: next });
    setConn("supplier", { connected: next.length > 0, connectedAt: Date.now() });
  }
  // one-time migration: the old single-supplier config becomes the first entry
  React.useEffect(() => {
    const old = conns.supplier || {};
    if (!list.length && (old.name || old.email)) {
      save([{ id: "sup" + Date.now().toString(36), name: old.name || "", email: old.email || "", accountNo: old.accountNo || "" }]);
    }
  }, []);
  function upsert() {
    const s = editing || {};
    if (!String(s.name || "").trim()) return;
    const next = s.id && list.some((x: any) => x.id === s.id)
      ? list.map((x: any) => (x.id === s.id ? { ...x, ...s } : x))
      : [...list, { ...s, id: "sup" + Date.now().toString(36) }];
    save(next); setEditing(null);
    store.addActivity(`Saved supplier — ${s.name}`);
  }
  async function remove(s: any) {
    if (!(await confirmDialog({ title: `Remove supplier ${s.name || ""}?`, body: "Saved order details for them go away.", confirmLabel: "Remove supplier", danger: true }))) return;
    save(list.filter((x: any) => x.id !== s.id));
    if (editing && editing.id === s.id) setEditing(null);
  }
  // a plain function CALLED inline ({field(...)}), never a <Component/> — a
  // nested component is a new type each render, so React would remount the
  // <input> every keystroke and drop focus after the first letter.
  const field = ({ k, label, ph, flex }: any) => (
    <div key={k} style={{ flex: flex || 1, minWidth: 140 }}>
      <label className="field-label">{label}</label>
      <input className="input" value={(editing && editing[k]) || ""} placeholder={ph} onChange={e => setEditing((s: any) => ({ ...s, [k]: e.target.value }))} />
    </div>
  );
  return (
    <div style={{ marginTop: 14 }}>
      {list.length > 0 && (
        <div style={{ display: "grid", gap: 6, marginBottom: 10 }}>
          {list.map((s: any) => (
            <div key={s.id} className="row between" style={{ padding: "8px 10px", background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: 10, gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{s.name}</div>
                <div className="muted" style={{ fontSize: 11.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {[s.email, s.accountNo && "acct " + s.accountNo, s.rep].filter(Boolean).join(" · ") || "—"}
                </div>
              </div>
              <div className="row gap-6" style={{ flexShrink: 0 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditing({ ...s })}><Icon name="edit" size={12} /></button>
                <button className="btn btn-ghost btn-sm" style={{ color: "var(--red)" }} onClick={() => remove(s)}><Icon name="trash" size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing ? (
        <div className="integ-config" style={{ display: "grid", gap: 10 }}>
          <div className="row gap-10" style={{ flexWrap: "wrap" }}>
            {field({ k: "name", label: "Supplier name", ph: "Midwest Fence Supply", flex: 2 })}
            {field({ k: "accountNo", label: "Account #", ph: "your account number" })}
          </div>
          <div className="row gap-10" style={{ flexWrap: "wrap" }}>
            {field({ k: "email", label: "Order email", ph: "orders@supplier.com", flex: 2 })}
            {field({ k: "phone", label: "Phone", ph: "(920) 555-0148" })}
          </div>
          <div className="row gap-10" style={{ flexWrap: "wrap" }}>
            {field({ k: "rep", label: "Your rep", ph: "rep's name" })}
            {field({ k: "notes", label: "Notes", ph: "delivery days, terms…", flex: 2 })}
          </div>
          <div className="row gap-8">
            <button className="btn btn-primary btn-sm" disabled={!String((editing && editing.name) || "").trim()} onClick={upsert}><Icon name="check" size={13} />{editing.id ? "Save changes" : "Add supplier"}</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className={"btn btn-sm " + (list.length ? "btn-outline" : "btn-primary btn-block")} onClick={() => setEditing({})}>
          <Icon name="bolt" size={13} />{list.length ? "Add another supplier" : "Add your first supplier"}
        </button>
      )}
    </div>
  );
}

// providers whose live connect is an OAuth handshake handled by the service
const OAUTH_PROVIDERS = ["stripe", "quickbooks", "gcal", "gbp", "gsheets", "gmail", "m365", "facebook"];
// fields that are secrets — sent to the service (→ Vault), never kept client-side
// (Stripe has none: it only ever connects through Stripe's own sign-in)
const SECRET_FIELDS = { twilio: ["authToken"], retell: ["apiKey"], resend: ["apiKey"], crm: ["apiKey"], jobber: ["apiKey"], wisetack: ["apiKey"] };

/* ---- why did this actually fail? ------------------------------------------
   The banner can only carry a short reason code, and two of those codes used to
   be guesses: "Stripe Connect isn't switched on" was a regex over Stripe's
   prose, and "isn't switched on platform-side" never said WHICH setting. This
   asks the service instead, and prints the three things that decide whether any
   connect can work at all — whether Stripe Connect is on, whether SERVICE_URL
   is an address a provider can call back to, and the exact redirect_uri the
   provider must have registered. Platform-owner only. */
const GOOGLE_IDS = ["gmail", "gcal", "gsheets", "gbp"];   // one OAuth client between them

/* How long ago, in words. A recorded failure with no age reads as current
   status, which is exactly how a fixed problem keeps looking unfixed. */
function ago(at: any) {
  const ms = Date.now() - new Date(at).getTime();
  if (!isFinite(ms) || ms < 0) return "just now";
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return m + (m === 1 ? " minute ago" : " minutes ago");
  const h = Math.round(m / 60);
  if (h < 24) return h + (h === 1 ? " hour ago" : " hours ago");
  const d = Math.round(h / 24);
  return d + (d === 1 ? " day ago" : " days ago");
}

function ConnectDiagnosis({ providerId }: any) {
  const DB: any = (window as any).VFP_DB;
  const [st, setSt] = React.useState<any>({ s: "idle" });
  const [copied, setCopied] = React.useState<any>(false);
  const run = React.useCallback(() => {
    if (!DB || !DB.ops || !DB.ops.enabled || !DB.ops.enabled()) { setSt({ s: "error", msg: "Needs the live service." }); return; }
    setSt({ s: "loading" });
    DB.ops.connectReadiness()
      .then((d: any) => setSt({ s: "ok", ...d }))
      .catch((e: any) => setSt({ s: "error", msg: String((e && e.message) || e) }));
  }, []);
  if (st.s === "idle") return <button className="btn btn-ghost btn-sm" onClick={run}><Icon name="activity" size={13} />Why did it fail?</button>;
  if (st.s === "loading") return <span className="faint" style={{ fontSize: 12 }}>Checking…</span>;
  if (st.s === "error") return <span style={{ fontSize: 12, color: "var(--amber)" }}>Couldn't check — {st.msg}</span>;

  const cfg = st.config || {};
  const me = (cfg.providers || []).find((x: any) => x.id === providerId);
  const stripe = st.stripe || {};
  const last = (st.lastErrors || {})[providerId];
  const urlBad = cfg.serviceUrlPublic === false;
  const googleUris = (cfg.providers || []).filter((x: any) => GOOGLE_IDS.includes(x.id)).map((x: any) => x.redirectUri);
  const copy = (t: string) => { try { navigator.clipboard.writeText(t); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch (e) {} };

  return (
    <div className="stripe-diag">
      <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
        {providerId === "stripe" && (() => {
          /* The recorded CREATE refusal outranks the read-only probe — the probe
             can list connected accounts before Connect sign-up is finished, so
             on its own it is not evidence that creating one will work. */
          const LABEL: any = {
            connect_not_signed_up: "Connect sign-up not finished",
            platform_profile_incomplete: "Platform profile incomplete",
            live_connect_in_review: "Live accounts pending Stripe review",
            bad_key: "Stripe key rejected",
            bad_url: "Stripe refused a URL we sent",
          };
          const refused = last && LABEL[last.verdict];
          const on = !refused && stripe.connectEnabled === true;
          return (
            <span className="chip" style={{ fontSize: 10.5, color: on ? "var(--green)" : "var(--amber)", borderColor: on ? "var(--green)" : "var(--amber)" }}>
              {/* a recorded failure is history, not status — say so, or someone
                  who has just fixed the cause reads it as still broken */}
              {refused ? "Last attempt: " + LABEL[last.verdict] : on ? "Connect is ON" : "Connect: not proven"}
            </span>
          );
        })()}
        <span className="chip" style={{ fontSize: 10.5, color: urlBad ? "var(--amber)" : "var(--green)", borderColor: urlBad ? "var(--amber)" : "var(--green)" }}>
          {urlBad ? "SERVICE_URL not reachable" : "SERVICE_URL OK"}
        </span>
        {me && !me.ready && <span className="chip" style={{ fontSize: 10.5, color: "var(--amber)", borderColor: "var(--amber)" }}>env missing</span>}
        <button className="btn btn-ghost btn-sm" style={{ padding: "2px 8px" }} onClick={run}><Icon name="bolt" size={11} />Re-check</button>
      </div>

      {/* 1. the setting that breaks every provider at once */}
      {urlBad && (
        <p style={{ fontSize: 12, margin: "0 0 8px", color: "var(--amber)" }}>
          <b>SERVICE_URL is {cfg.serviceUrl}</b>{cfg.serviceUrlSet ? "" : " — unset, so this is the built-in default"}. Providers can't call that back, which is exactly what Google reports as <code className="merge">redirect_uri_mismatch</code>. Set it to the service's public https origin in <code className="merge">server/.env</code> and restart.
        </p>
      )}

      {/* 2. what this provider is missing */}
      {me && !me.ready && (
        <p style={{ fontSize: 12, margin: "0 0 8px" }}>
          Not set in <code className="merge">server/.env</code>: <b style={{ color: "var(--amber)" }}>{me.missingEnv.join(", ")}</b>. That's the whole of "isn't switched on platform-side" for this one.
        </p>
      )}
      {me && me.pathNote && <p className="faint" style={{ fontSize: 11, margin: "0 0 8px" }}>Path in use: {me.pathNote}.</p>}

      {/* 3. the string the provider must have registered, verbatim */}
      {me && (
        <div style={{ marginBottom: 8 }}>
          <div className="field-label" style={{ margin: "0 0 4px" }}>Redirect URI this service sends</div>
          <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
            <code className="mono" style={{ fontSize: 11.5, background: "var(--sunken)", border: "1px solid var(--line)", borderRadius: 8, padding: "6px 9px", wordBreak: "break-all", flex: 1, minWidth: 0 }}>{me.redirectUri}</code>
            <button className="btn btn-ghost btn-sm" style={{ padding: "3px 9px" }} onClick={() => copy(me.redirectUri)}>{copied ? "Copied" : "Copy"}</button>
          </div>
          <p className="faint" style={{ fontSize: 11, margin: "5px 0 0" }}>It must match a registered redirect URI at the provider <b>character for character</b>{GOOGLE_IDS.includes(providerId) ? " — Google Cloud Console → Credentials → your OAuth client → Authorised redirect URIs" : ""}.</p>
        </div>
      )}

      {/* The four Google integrations share one OAuth client, so registering
          them one failure at a time is three needless round trips. */}
      {GOOGLE_IDS.includes(providerId) && (
        <ol className="faint" style={{ fontSize: 11.5, margin: "0 0 8px", paddingLeft: 18, display: "grid", gap: 3 }}>
          <li><a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener" style={{ color: "var(--teal)" }}>console.cloud.google.com → APIs &amp; Services → Credentials</a></li>
          <li>Pick the project holding the client id in <code className="merge">GOOGLE_CLIENT_ID</code>, then click that <b>OAuth 2.0 Client ID</b> (type: Web application)</li>
          <li>Under <b>Authorised redirect URIs</b> → <b>ADD URI</b> → paste → <b>Save</b>. Changes can take a few minutes to take effect.</li>
        </ol>
      )}
      {GOOGLE_IDS.includes(providerId) && googleUris.length > 1 && (
        <div style={{ marginBottom: 8 }}>
          <div className="row between" style={{ alignItems: "center", marginBottom: 4, flexWrap: "wrap", gap: 6 }}>
            <span className="field-label" style={{ margin: 0 }}>All {googleUris.length} Google URIs — add them in one visit</span>
            <button className="btn btn-ghost btn-sm" style={{ padding: "3px 9px" }} onClick={() => copy(googleUris.join("\n"))}>{copied ? "Copied" : "Copy all"}</button>
          </div>
          <div className="mono" style={{ fontSize: 11, background: "var(--sunken)", border: "1px solid var(--line)", borderRadius: 8, padding: "7px 9px", wordBreak: "break-all", lineHeight: 1.65 }}>
            {googleUris.map((u: string) => <div key={u}>{u}</div>)}
          </div>
          <p className="faint" style={{ fontSize: 11, margin: "5px 0 0" }}>Gmail, Calendar, Sheets and Business Profile all sign in through the same OAuth client — one missing URI blocks that one integration only.</p>
        </div>
      )}

      {/* 4. Stripe's own words, when Stripe is the one refusing */}
      {providerId === "stripe" && (
        <>
          {/* what Stripe said when we actually tried — the authoritative bit */}
          {last && last.fix && (
            <p style={{ fontSize: 12.5, margin: "0 0 6px" }}><b style={{ color: "var(--amber)" }}>Fix:</b> {last.fix}</p>
          )}
          {/* Show the PAGE, not a generic label. Stripe has pointed at two
              different dashboard pages for this, and someone who has already
              completed one needs to see at a glance that this is the other. */}
          {last && last.docUrl && (
            <p style={{ margin: "0 0 6px", fontSize: 12 }}>
              <a href={last.docUrl} target="_blank" rel="noopener" style={{ color: "var(--teal)", wordBreak: "break-all" }}>
                Open {last.docUrl.replace(/^https?:\/\//, "")} →
              </a>
            </p>
          )}
          {stripe.detail && <p className="faint" style={{ fontSize: 11, margin: "0 0 6px", wordBreak: "break-word" }}>Read-only check: {stripe.detail}</p>}
        </>
      )}

      {last && (
        <>
          <p className="faint" style={{ fontSize: 11, margin: "6px 0 0" }}>
            Last failed attempt {new Date(last.at).toLocaleString()} ({ago(last.at)}) — {last.code ? last.code + ": " : ""}{last.message}
          </p>
          {/* Re-check is read-only: it asks Stripe what it can see, and cannot
              know whether a create would now succeed. Only a real attempt can,
              and that is Try again. Without saying so, someone who has just
              fixed the cause re-checks, sees the same old verdict, and
              concludes the fix didn't work. */}
          <p className="faint" style={{ fontSize: 11, margin: "4px 0 0", color: "var(--amber)" }}>
            Already fixed this? That line is the last <b>attempt</b>, not the current state — Re-check can't see it, because it doesn't create an account. Press <b>Try again</b> above to re-test for real.
          </p>
        </>
      )}
    </div>
  );
}

/* ---- what Stripe is still waiting for, in the app ----
   Stripe knows precisely which fields are outstanding on a connected account
   and says so on its own dashboard ("Actions required: Provide a business type
   / an external account / a representative"). We stored details_submitted and
   nothing else, so an account Stripe had restricted looked exactly like one
   nobody had started: "Not connected", no reason, no way back in. This reads
   the requirements and shows the list.

   The contractor's account is a STANDARD account — theirs, with their own
   login — so anything left to finish is finished in their own Stripe
   dashboard. A legacy Express account (created by Fence Works before the
   switch) is offered the move to their own account instead. */
function StripeStatus({ onSwitch, busy }: any) {
  const DB: any = window.VFP_DB;
  const [st, setSt] = React.useState<any>(null);
  const [err, setErr] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [opening, setOpening] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true); setErr(null);
    try { setSt(await DB.stripeAccountStatus()); }
    catch (e: any) { setErr(String((e && e.message) || e)); }
    finally { setLoading(false); }
  }, [DB]);
  React.useEffect(() => { load(); }, [load]);

  async function openDashboard() {
    setOpening(true);
    try {
      const url = await DB.stripeDashboardUrl();
      if (url) window.open(url, "_blank", "noopener");
    } catch (e: any) { notify((e && e.message) || "Couldn't open Stripe."); }
    finally { setOpening(false); }
  }

  if (loading) return <p className="faint" style={{ fontSize: 12, margin: "12px 0 0" }}>Checking with Stripe…</p>;
  if (err) return (
    <div className="stripe-st" style={{ marginTop: 12 }}>
      <p style={{ fontSize: 12.5, margin: 0, color: "var(--amber)" }}>Couldn't reach Stripe for the current status. {err}</p>
      <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={load}><Icon name="undo" size={13} />Re-check</button>
    </div>
  );
  if (st && st.revoked) return (
    <div className="stripe-st" style={{ marginTop: 12 }}>
      <p style={{ fontSize: 12.5, margin: 0, color: "var(--amber)" }}>Fence Works was disconnected from your Stripe account (from inside Stripe). Connect again to turn pay buttons back on — nothing about your Stripe account changed.</p>
    </div>
  );
  if (!st || !st.configured || !st.started) return null;

  const n = (st.dueNow || []).length;
  const legacy = !!st.legacyExpress;
  const achOff = st.capabilities && st.capabilities.achPayments === "inactive";
  return (
    <div className="stripe-st">
      <div className="stripe-st-head">
        <span className={"chip " + (st.chargesEnabled ? "green" : "amber")} style={{ fontSize: 10.5 }}>
          {st.chargesEnabled ? (st.ready ? "Live — taking payments" : "Taking payments") : n ? `${n} thing${n === 1 ? "" : "s"} Stripe still needs` : "Setup unfinished"}
        </span>
        {/* charges and payouts fail independently: money can come in while the
            route out is still blocked, and that is worth seeing separately */}
        <span className="faint" style={{ fontSize: 11 }}>
          Payments {st.chargesEnabled ? "on" : "off"} · Payouts {st.payoutsEnabled ? "on" : "off"}
          {st.payoutSchedule ? ` · ${st.payoutSchedule} payouts` : ""}
        </span>
      </div>

      {legacy && (
        <p className="stripe-st-why" style={{ color: "var(--amber)" }}>
          This is a Fence Works–managed Stripe Express account from before we switched to letting you use your own. Connect your own Stripe account so the money, the fees, refunds and disputes all sit in your Stripe dashboard. Past payouts on this one stay at <a href="https://connect.stripe.com/express_login" target="_blank" rel="noopener" style={{ color: "var(--teal)" }}>connect.stripe.com/express_login</a>.
        </p>
      )}

      {st.disabledCopy && <p className="stripe-st-why">{st.disabledCopy}</p>}
      {st.deadline && !st.ready && (
        <p className="stripe-st-why" style={{ color: "var(--amber)" }}>
          Stripe's deadline: {new Date(st.deadline).toLocaleDateString()}
        </p>
      )}

      {/* Stripe's own reason for rejecting something already submitted — without
          it, "upload a photo ID" reads as a loop to someone who just did */}
      {(st.errors || []).map((e: any, i: number) => (
        <p key={i} className="stripe-st-err"><Icon name="bolt" size={12} style={{ flexShrink: 0, marginTop: 2 }} /><span><b>{e.label}:</b> {e.reason}</span></p>
      ))}

      {n > 0 && (
        <ul className="stripe-st-list">
          {st.dueNow.map((t: any) => (
            <li key={t.id}>
              <Icon name="x" size={12} style={{ color: t.pastDue ? "var(--red)" : "var(--amber)", flexShrink: 0, marginTop: 2 }} />
              <span>{t.label}{t.pastDue && <span className="stripe-st-tag">past due</span>}</span>
            </li>
          ))}
        </ul>
      )}

      {(st.pendingVerification || []).length > 0 && (
        <p className="stripe-st-why">Stripe is verifying: {st.pendingVerification.join(", ")}. Nothing to do while that runs.</p>
      )}

      {(st.dueLater || []).length > 0 && (
        <p className="faint" style={{ fontSize: 11.5, margin: "8px 0 0" }}>
          Later: {st.dueLater.map((t: any) => t.label).join(", ")}.
        </p>
      )}

      {/* the no-fee "pay by bank transfer" button needs ACH on THEIR account */}
      {achOff && !legacy && (
        <p className="stripe-st-why">Bank transfer (ACH) is off on your Stripe account, so customers can only pay by card. Turn on <b>ACH Direct Debit</b> in your Stripe dashboard under Settings → Payment methods.</p>
      )}

      <div className="row gap-8" style={{ marginTop: 12, flexWrap: "wrap" }}>
        {legacy
          ? <button className="btn btn-primary btn-sm" disabled={!!busy} onClick={onSwitch}>
              <Icon name="bolt" size={13} />{busy ? "Opening…" : "Use my own Stripe account"}</button>
          : !st.ready && <button className="btn btn-primary btn-sm" disabled={opening} onClick={openDashboard}>
              <Icon name="bolt" size={13} />{opening ? "Opening…" : "Finish in your Stripe dashboard"}</button>}
        {(legacy ? st.detailsSubmitted : st.ready) && <button className="btn btn-ghost btn-sm" disabled={opening} onClick={openDashboard}>
          <Icon name="dollar" size={13} />{opening ? "Opening…" : "Open Stripe dashboard"}</button>}
        <button className="btn btn-ghost btn-sm" onClick={load}><Icon name="undo" size={13} />Re-check</button>
      </div>
      {!legacy && <p className="faint" style={{ fontSize: 11, margin: "8px 0 0" }}>Refunds, disputes, payouts and reports all live in your own Stripe dashboard. Fence Works never holds your money and takes no cut.</p>}
    </div>
  );
}

/* ---- the page we own, right before the hand-off to Stripe ----
   Stripe's sign-in and sign-up pages are Stripe's; only its logo and colours
   are ours to set. So the explaining happens here instead. Three things a
   contractor actually wants to know before handing over an SSN: what happens
   next, what to have in hand, and who ends up holding the money.

   Two doors, because there are two kinds of contractor: one who already runs
   Stripe (sign in, pick the account — nothing to re-enter) and one who doesn't
   (Stripe's sign-up, pre-filled with what we know). */
function StripeHandoff({ onGo, onClose, busy, company }: any) {
  return (
    <Portal>
      <div className="modal-scrim" data-dialog onClick={(e: any) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="modal sha" role="dialog" aria-modal="true" aria-labelledby="sha-title">
          <div className="sha-top">
            <div className="sha-hop">
              <CompanyLogo company={company} size={28} />
              <span className="sha-hop-name">{company && company.name ? company.name : "Fence Works"}</span>
              <span className="sha-hop-run" />
              <Icon name="arrow" size={15} style={{ color: "var(--fg-3)", flexShrink: 0 }} />
              <span className="sha-hop-run" />
              <span className="sha-hop-name">Stripe</span>
              <span className="sha-badge"><Icon name="lock" size={17} /></span>
            </div>
            <h2 className="sha-title" id="sha-title">Take card and bank payments — into your own Stripe account</h2>
            <p className="sha-sub">Your Stripe account, your login, your money. Fence Works just puts a pay button on your quotes.</p>
            {/* the question every contractor asks first, answered before they ask */}
            <div className="sha-flow">
              <div><b>Customer pays</b>card or bank</div>
              <div><b>Your Stripe balance</b>same day</div>
              <div><b>Your bank</b>on your payout schedule</div>
            </div>
          </div>

          <div className="sha-body">
            <p className="sha-h">What happens next</p>
            <ol className="sha-steps">
              <li><b>Already use Stripe?</b> Sign in and pick the account to use. That's it.</li>
              <li><b>New to Stripe?</b> Create your account — about 10 minutes, and we've pre-filled what we already know.</li>
              <li>You land back here, and pay buttons switch on for every quote, invoice and customer hub link.</li>
            </ol>

            <p className="sha-h">New account? Have these handy</p>
            <ul className="sha-need">
              <li><Icon name="building" size={14} style={{ color: "var(--teal)", flexShrink: 0, marginTop: 1 }} />Legal business name and address</li>
              <li><Icon name="clipboard" size={14} style={{ color: "var(--teal)", flexShrink: 0, marginTop: 1 }} />EIN — or your SSN if you're a sole proprietor</li>
              <li><Icon name="dollar" size={14} style={{ color: "var(--teal)", flexShrink: 0, marginTop: 1 }} />Bank account and routing number for payouts</li>
              <li><Icon name="user" size={14} style={{ color: "var(--teal)", flexShrink: 0, marginTop: 1 }} />A photo ID for the owner</li>
            </ul>

            <div className="sha-safe">
              <Icon name="lock" size={14} style={{ color: "var(--green)", flexShrink: 0, marginTop: 1 }} />
              <span>Stripe collects all of that, not us — Fence Works never sees card numbers, your bank login or your ID, and never holds your money. Refunds, disputes and payouts are yours to run from your own Stripe dashboard. <b>We take no cut.</b> Stripe's own processing fee is the only deduction.</span>
            </div>
          </div>

          <div className="sha-foot">
            <button className="btn btn-primary" disabled={!!busy} onClick={() => onGo("login")}>
              <Icon name="bolt" size={14} />{busy ? "Opening Stripe…" : "I have a Stripe account →"}
            </button>
            <button className="btn btn-outline" disabled={!!busy} onClick={() => onGo("register")}>
              Create a Stripe account
            </button>
            <button className="btn btn-ghost" onClick={onClose}>Not now</button>
          </div>
        </div>
      </div>
    </Portal>
  );
}

function Integrations({ store, go }: any) {
  const [conns, setConn] = useIntegrations(store);
  const [openId, setOpenId] = React.useState<any>(null);
  const [busy, setBusy] = React.useState<any>(null);
  const connectedCount = INTEGRATIONS.filter(i => conns[i.id] && conns[i.id].connected).length;
  const DB = window.VFP_DB;
  const liveService = !!(DB && DB.apiBase && DB.apiBase());
  const [notice, setNotice] = React.useState<any>(null);
  /* the Stripe explainer is a step in the flow, not a settings page: it holds
     the integration it was opened for, and hands it straight to doConnect */
  const [handoff, setHandoff] = React.useState<any>(null);
  // useSupportAdmin() hands back { admin, inboxUnread } — the OBJECT is always
  // truthy, so the flag is the .admin inside it (same shape the sidebar reads)
  const supportAdmin = useSupportAdmin();
  const isPlatformOwner = !!(supportAdmin && supportAdmin.admin);   // only the platform owner can act on a platform-side failure

  // OAuth round-trip result (?connected=stripe&ok=1[&reason=…]) → banner, and
  // on success mirror the server-side integration row into settings so the
  // chip / dashboard rails light up without a reload.
  React.useEffect(() => {
    let sp; try { sp = new URLSearchParams(window.location.search); } catch (e) { return; }
    const p = sp.get("connected");
    if (!p) return;
    const ok = sp.get("ok") === "1";
    const it = INTEGRATIONS.find(x => x.id === p);
    setNotice({ id: p, name: (it && it.name) || p, ok, reason: sp.get("reason") || "" });
    if (ok) setConn(p, { connected: true, connectedAt: Date.now() });
    try { window.history.replaceState({}, "", window.location.pathname); } catch (e) {}
  }, []);

  /* The chips, the dashboard checklist and the quote nudge read the LOCAL
     connected flag, but Stripe decides it: a new account is linked before
     Stripe switches charges on, and a contractor can revoke us from inside
     their own Stripe dashboard. Mirror the server's answer once per visit,
     and only write when it actually differs. (Non-admins get a 403 — ignored.) */
  React.useEffect(() => {
    if (!liveService || !DB || !DB.stripeAccountStatus) return;
    let live = true;
    DB.stripeAccountStatus().then((st: any) => {
      if (!live || !st || !st.configured) return;
      const want = !!(st.started && st.chargesEnabled);
      const have = !!(conns.stripe && conns.stripe.connected);
      if (want !== have) setConn("stripe", want ? { connected: true, connectedAt: Date.now() } : { connected: false });
    }).catch(() => {});
    return () => { live = false; };
  }, []);

  async function doConnect(it, skipIntro = false, opts: any = null) {
    const c = conns[it.id] || {};
    /* 0) Stripe asks for an SSN and a bank account on a page that is Stripe's,
       not ours. Explain it first, on a page that IS ours. Retries skip this —
       whoever is pressing Try again has already read it. */
    if (!skipIntro && it.id === "stripe" && liveService && OAUTH_PROVIDERS.includes(it.id) && DB.oauthStart) {
      setHandoff(it);
      return;
    }
    // 1) OAuth provider + live service → fetch a one-time start URL (authed),
    //    then hand off to the consent flow
    if (liveService && OAUTH_PROVIDERS.includes(it.id) && DB.oauthStart) {
      setBusy(it.id);
      try {
        const url = await DB.oauthStart(it.id, opts || undefined);
        if (url === false) return;   // read the Google heads-up and chose "Not now"
        if (url) { window.location.href = url; return; }
        // a 200 with no URL used to end here in silence — the button just
        // un-busied itself and the contractor was left staring at the card
        setNotice({ id: it.id, name: it.name, ok: false, reason: "platform_setup" });
      } catch (e) {
        setNotice({ id: it.id, name: it.name, ok: false, reason: "error", detail: String((e as any).message || e) });
      } finally { setBusy(null); }
      return;
    }
    // 2) API-key provider + live service → push secrets to Vault, keep config local
    if (liveService && DB.connectIntegration) {
      setBusy(it.id);
      try {
        const fields = {};
        it.fields.forEach(f => { if (c[f.key] != null && c[f.key] !== "") fields[f.key] = c[f.key]; });
        await DB.connectIntegration(it.id, fields);
        const patch = { connected: true, connectedAt: Date.now() };
        (SECRET_FIELDS[it.id] || []).forEach(k => { patch[k] = undefined; }); // forget the local secret
        setConn(it.id, patch);
        store.addActivity(`Connected ${it.name}`);
        setOpenId(null);
      } catch (e) {
        notify("Couldn't connect " + it.name + ": " + (e.message || e));
      } finally { setBusy(null); }
      return;
    }
    // 3) no service configured → local demo connect (stores config locally only)
    setConn(it.id, { connected: true, connectedAt: Date.now() });
    store.addActivity(`Connected ${it.name}`);
    setOpenId(null);
  }
  async function doDisconnect(it) {
    if (liveService && DB.disconnectIntegration) {
      // the server holds the real token: only flip local state once it's gone,
      // otherwise the card says "not connected" while the connection lives on
      setBusy(it.id);
      let res: any = null;
      try { res = await DB.disconnectIntegration(it.id); }
      catch (e: any) { notify("Couldn't disconnect " + it.name + ": " + ((e && e.message) || e)); return; }
      finally { setBusy(null); }
      // Google: our tokens are deleted; access at Google ends with the last Google service
      if (res && res.warning) notify(res.warning);
      else if (res && res.stillConnectedLabels && res.stillConnectedLabels.length) notify("Disconnected " + it.name + ". " + res.stillConnectedLabels.join(" and ") + " still use" + (res.stillConnectedLabels.length === 1 ? "s" : "") + " your Google account, so Fence Works keeps that access. Disconnect " + (res.stillConnectedLabels.length === 1 ? "it" : "them") + " too to remove it.");
      else if (res && res.revokedAtGoogle) notify("Disconnected " + it.name + ". Fence Works no longer has access to your Google account.");
    }
    setConn(it.id, { connected: false });
  }

  return (
    <div className="page fade-up">
      <div className="row between" style={{ marginBottom: 6, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 className="display" style={{ fontSize: 28, margin: 0 }}>Integrations</h1>
          <p className="muted" style={{ margin: "4px 0 0" }}>Connect Fence Works to the tools you already run on. {connectedCount} of {INTEGRATIONS.length} connected.</p>
          {/* FW-026: this claimed SMS needed nothing while smsReady() refuses to
              send until A2P 10DLC registration is approved — a real, carrier-run,
              multi-day process. Saying "nothing to connect" turned a known wait
              into a mystery: texts simply never arrived. */}
          <p className="faint" style={{ margin: "6px 0 0", fontSize: 12 }}>Email and the AI voice receptionist are built into Fence Works — nothing to connect for those. <b>Texting needs a one-time carrier registration</b> (A2P 10DLC, required by US carriers for any business texting) — start it under Messages → Texts.</p>
        </div>
      </div>

      {handoff && (
        <StripeHandoff
          company={(store && store.company) || {}}
          busy={busy === handoff.id}
          onClose={() => setHandoff(null)}
          onGo={(landing: string) => { const it = handoff; setHandoff(null); doConnect(it, true, { landing }); }}
        />
      )}

      {notice && (() => {
        /* FW: this banner used to be a dead end — it explained the failure and
           then left you with nothing to press, so "Stripe didn't connect" was
           where the flow stopped. Every failure now carries the one button that
           moves it forward (retry the same provider), plus the exact fix link
           when the failure is platform-side. */
        const noticeIt = INTEGRATIONS.find(x => x.id === notice.id);
        const canRetry = !notice.ok && !!noticeIt && notice.reason !== "denied" && notice.reason !== "stripe_pending";
        const copy = notice.ok ? "You're live — everything wired to this integration is active now."
          : notice.reason === "stripe_connect_off" ? "Payments run through YOUR Stripe account — the money, the fees and any refunds all stay on your side. To link it, Fence Works needs a one-time registration with Stripe that we haven't finished yet. Nothing is wrong with your Stripe login, and nothing routes through us. Until it's done, quotes and the customer hub still send, they just can't take card payments."
          : notice.reason === "service_url" ? "This service isn't configured with a public address yet, so the provider has nowhere to send you back to. Nothing wrong on your end — we're fixing it."
          : notice.reason === "platform_setup" ? "This one isn't switched on platform-side yet — we're finishing setup with the provider. Nothing wrong on your end."
          : notice.reason === "incomplete" ? "The provider's onboarding wasn't finished — pick up where you left off and finish the business details Stripe asked for."
          : notice.reason === "stripe_pending" ? "Your Stripe account is linked, but Stripe hasn't switched on payments for it yet. Open Payment setup to see exactly what Stripe still needs — you finish it in your own Stripe dashboard, and pay buttons turn on by themselves once Stripe clears it."
          : notice.reason === "denied" ? "The authorization was cancelled or denied on the provider's page. Nothing changed — start it again whenever you're ready."
          : notice.reason === "state" ? "The connect link expired before it came back. Start it again — it only takes a moment."
          : "The handshake didn't complete. Try again — if it keeps failing, tell us and we'll look at it from our side.";
        return (
        <div className="card pad connect-notice" style={{ borderColor: notice.ok ? "var(--green)" : "var(--amber)" }}>
          <Icon name={notice.ok ? "check" : "bolt"} size={16} style={{ color: notice.ok ? "var(--green)" : "var(--amber)", flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <b style={{ fontSize: 13.5 }}>{notice.ok ? `${notice.name} connected.` : `${notice.name} didn't connect.`}</b>
            <p className="muted" style={{ margin: "3px 0 0", fontSize: 12.5 }}>{copy}</p>
            {notice.detail && <p className="faint mono" style={{ margin: "4px 0 0", fontSize: 11, wordBreak: "break-word" }}>{notice.detail}</p>}
            {!notice.ok && (
              <div className="row gap-8" style={{ marginTop: 10, flexWrap: "wrap" }}>
                {canRetry && <button className="btn btn-primary btn-sm" disabled={busy === notice.id}
                  onClick={() => { setNotice(null); doConnect(noticeIt, true); }}>
                  <Icon name="bolt" size={13} />{busy === notice.id ? "Starting…" : notice.reason === "incomplete" ? "Finish setup" : "Try again"}</button>}
                {notice.reason === "denied" && noticeIt && <button className="btn btn-primary btn-sm" disabled={busy === notice.id}
                  onClick={() => { setNotice(null); doConnect(noticeIt, true); }}><Icon name="bolt" size={13} />Start over</button>}
                {(notice.reason === "stripe_connect_off" || notice.reason === "platform_setup" || notice.reason === "service_url") && !isPlatformOwner &&
                  <button className="btn btn-ghost btn-sm" onClick={() => window.dispatchEvent(new Event("fw:help-open"))}><Icon name="chat" size={13} />Tell us it's blocking you</button>}
                {notice.reason === "stripe_pending" && <button className="btn btn-primary btn-sm" onClick={() => { setNotice(null); setOpenId("stripe"); }}>
                  <Icon name="gear" size={13} />Open Payment setup</button>}
                {isPlatformOwner && notice.reason !== "stripe_pending" && <ConnectDiagnosis providerId={notice.id} />}
                {notice.id === "stripe" && notice.reason !== "stripe_pending" && <button className="btn btn-ghost btn-sm" onClick={() => go && go("financing")}><Icon name="dollar" size={13} />Offer financing instead</button>}
              </div>
            )}
          </div>
          <button className="btn btn-ghost btn-sm" aria-label="Dismiss" onClick={() => setNotice(null)}><Icon name="x" size={13} /></button>
        </div>
        );
      })()}

      <div className="integ-list">
        {INTEGRATIONS.map(it => {
          const c = conns[it.id] || {};
          const connected = !!c.connected;
          const open = openId === it.id;
          const soon = !!it.soon && !connected;
          const hasConfig = it.fields.length > 0;
          const toggle = () => setOpenId(open ? null : it.id);
          return (
            <div key={it.id} className={"integ-item" + (connected ? " on" : "") + (open ? " open" : "")}>
              <ListRow className="integ-row" muted={soon} onClick={toggle} ariaLabel={it.name + (open ? " — collapse" : " — expand")}
                leading={<div className="integ-logo sm" style={{ background: it.color + "22", color: it.color, borderColor: it.color + "55" }}><Icon name={it.icon} size={16} /></div>}
                title={<><span>{it.name}</span><span className="muted" style={{ fontWeight: 500, fontSize: 12 }}>{it.cat}</span></>}
                sub={<span className={open ? "" : "clamp2"}>{it.blurb}</span>}
                trailing={<>
                  {connected
                    ? <span className="chip green" style={{ fontSize: 10.5 }}><span style={{ width: 6, height: 6, borderRadius: 99, background: "var(--green)" }} />Connected</span>
                    : soon
                      ? <span className="chip amber" style={{ fontSize: 10.5 }}>Coming soon</span>
                      : <span className="chip" style={{ fontSize: 10.5 }}>Not connected</span>}
                  <span className="chip" style={{ fontSize: 10.5, cursor: "help" }} title={it.scopes.join("\n")}>{it.scopes.length} scope{it.scopes.length === 1 ? "" : "s"}</span>
                  {it.id === "wisetack"
                    ? <button className="btn btn-primary btn-sm" onClick={() => go && go("financing")}><Icon name="dollar" size={13} />{connected ? "Manage on Financing" : "Set up on Financing"}</button>
                    : it.id === "supplier"
                      ? <button className={"btn btn-sm " + (open ? "btn-ghost" : "btn-primary")} onClick={toggle}><Icon name="gear" size={13} />{open ? "Close" : "Manage suppliers"}</button>
                      : connected
                        ? <>
                            {/* only offer "Manage" when there's something to configure — an
                                OAuth integration with no fields (Sheets, Gmail, M365, Stripe)
                                has nothing to manage here. Switch accounts via Disconnect → connect. */}
                            {/* Stripe has no config fields, but it does now have a
                                live setup checklist worth opening */}
                            {(hasConfig || it.id === "stripe") && <button className="btn btn-ghost btn-sm" onClick={toggle}><Icon name="gear" size={13} />{open ? "Close" : it.id === "stripe" ? "Payment setup" : "Manage"}</button>}
                            <button className="btn btn-sm" style={{ background: "var(--red-tint)", color: "var(--red)" }} disabled={busy === it.id} onClick={() => doDisconnect(it)}>Disconnect</button>
                          </>
                        : soon
                          ? <button className="btn btn-ghost btn-sm" disabled><Icon name="bolt" size={13} />Coming soon</button>
                          /* OAuth providers connect in ONE click — straight to the provider's
                             consent screen. Optional config (e.g. gcal's Calendar ID) moves to
                             the post-connect Manage panel instead of gating the connect. */
                          : <button className="btn btn-primary btn-sm" disabled={busy === it.id}
                              onClick={() => ((it.fields.length === 0 || OAUTH_PROVIDERS.includes(it.id)) ? doConnect(it) : setOpenId(it.id))}>
                              <Icon name="bolt" size={13} />{busy === it.id ? "Connecting…" : "Connect"}</button>}
                  <Icon name={open ? "chevdown" : "chevright"} size={15} className="muted" />
                </>} />
              {open && (
                <div className="integ-expand">
                  <div className="integ-scopes" style={{ borderTop: 0, padding: 0 }}>
                    {it.scopes.map(s => <div key={s} className="row gap-8" style={{ fontSize: 12, color: "var(--fg-2)" }}><Icon name="check" size={12} style={{ color: connected ? "var(--green)" : "var(--fg-faint)" }} />{s}</div>)}
                  </div>
                  {it.dev && <p className="row gap-6" style={{ fontSize: 11.5, color: "var(--amber)", margin: "12px 0 0", alignItems: "flex-start" }}><Icon name="bolt" size={12} style={{ flexShrink: 0, marginTop: 1 }} />{it.dev}</p>}
                  {soon && <p className="row gap-6" style={{ fontSize: 11.5, color: "var(--amber)", margin: "12px 0 0", alignItems: "flex-start" }}><Icon name="bolt" size={12} style={{ flexShrink: 0, marginTop: 1 }} />{it.soon}</p>}
                  {it.id === "supplier" ? <SupplierManager store={store} conns={conns} setConn={setConn} /> : <>
                  {it.id === "stripe" && liveService && <StripeStatus busy={busy === it.id} onSwitch={() => doConnect(it)} />}
                  {it.id === "gbp" && connected && liveService && <GBPLocationPicker />}
                  {it.id === "facebook" && connected && liveService && <FacebookPagePicker />}
                  {(hasConfig || (!connected && !soon && it.id !== "wisetack")) && (
                    <div className="integ-config">
                      {it.fields.map(f => (
                        <div key={f.key} style={{ marginBottom: 10 }}>
                          <label className="field-label">{f.label}</label>
                          <input className="input" value={(c[f.key]) || ""} placeholder={f.ph} onChange={e => setConn(it.id, { [f.key]: e.target.value })} />
                        </div>
                      ))}
                      <div className="row gap-8" style={{ flexWrap: "wrap" }}>
                        {OAUTH_PROVIDERS.includes(it.id) && connected
                          /* connected OAuth: this panel is SETTINGS, not auth — field edits
                             sync automatically, so Save just confirms & closes. Re-auth is
                             the rare secondary action (switch account / restore access). */
                          ? <>
                              <button className="btn btn-primary btn-sm" onClick={() => setOpenId(null)}><Icon name="check" size={13} />Save</button>
                              <button className="btn btn-ghost btn-sm" disabled={busy === it.id} onClick={() => doConnect(it, true)}>{busy === it.id ? "Connecting…" : "Re-authorize"}</button>
                            </>
                          : <button className="btn btn-primary btn-sm" disabled={busy === it.id} onClick={() => doConnect(it)}>
                              <Icon name="check" size={13} />{busy === it.id ? "Connecting…" : (liveService && OAUTH_PROVIDERS.includes(it.id)) ? `Authorize with ${it.name}` : connected ? "Save" : "Authorize & connect"}
                            </button>}
                        <button className="btn btn-ghost btn-sm" onClick={() => setOpenId(null)}>Cancel</button>
                      </div>
                      <p className="faint" style={{ fontSize: 11, marginTop: 8 }}>{OAUTH_PROVIDERS.includes(it.id) && connected
                        ? "Changes save automatically. Re-authorize only to switch the connected account or restore access you revoked on the provider."
                        : liveService
                          ? (OAUTH_PROVIDERS.includes(it.id) ? "Live OAuth — you'll authorize on the provider, then land back here connected." : "Live connection — secrets are stored server-side in Vault, never in the browser or database.")
                          : "Demo connection — stores config locally so the flow is wired end-to-end. Set window.VFP_API_BASE to a deployed automation service for live OAuth + Vault."}</p>
                    </div>
                  )}
                  </>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   Customer Hub — settings + per-customer secure link
   ============================================================ */
function genToken() {
  const a = new Uint8Array(16);
  (window.crypto || {}).getRandomValues ? window.crypto.getRandomValues(a) : a.forEach((_, i) => a[i] = Math.floor(Math.random() * 256));
  return Array.from(a, b => b.toString(16).padStart(2, "0")).join("");
}
function hubUrl(token) {
  // clean-URL route: always origin + /hub/<token> (the old split("#")[0]
  // produced "…/integrationshub/<token>" once we dropped the hash router)
  return location.origin + "/hub/" + token;
}

function CustomerHubSettings({ store, go }: any) {
  const enabled = !((store.settings || {}).hubDisabled);
  const customers = store.customers || [];
  const [copied, setCopied] = React.useState<any>(null);
  const [preview, setPreview] = React.useState<any>(null);   // token to preview in-app

  function ensureToken(c) {
    if (c.hubToken) return c.hubToken;
    const t = genToken();
    store.updateCustomer(c.id, { hubToken: t });
    return t;
  }
  function copyLink(c) {
    const t = ensureToken(c);
    const url = hubUrl(t);
    navigator.clipboard && navigator.clipboard.writeText(url);
    setCopied(c.id); setTimeout(() => setCopied(null), 1800);
  }
  function emailLink(c) {
    const t = ensureToken(c);
    const url = hubUrl(t);
    const company = (store.company && store.company.name) || (store.settings && store.settings.companyName) || "your contractor";
    const subject = `Your ${company} project portal`;
    const body = `Hi ${(c.name || "there").split(" ")[0]},\n\n`
      + `Here's your private project link with ${company} — track your fence job, view your quote & invoice, pay, and message us anytime:\n\n${url}\n\n`
      + `Please don't share it; anyone with the link can view your project.\n\n— ${company}`;
    window.location.href = `mailto:${encodeURIComponent(c.email || "")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }
  async function rotateLink(c) {
    if (!(await confirmDialog({ title: `Rotate ${c.name || "this customer"}'s link?`, body: "The current link stops working immediately — anyone you've already shared it with will need the new one.", confirmLabel: "Rotate link", danger: true }))) return;
    store.updateCustomer(c.id, { hubToken: genToken() });
  }
  function openPreview(c) {
    const t = c.hubToken || genToken();
    if (!c.hubToken) store.updateCustomer(c.id, { hubToken: t });
    setPreview(t);
  }
  function shortLink(token) {
    // show a clean, shareable-looking tail rather than the long sandbox proxy URL
    const host = (location.hostname || "fenceworks.ai").replace(/^www\./, "");
    return host + "/hub/" + token.slice(0, 8) + "…";
  }

  return (
    <div className="page fade-up">
      <h1 className="display" style={{ fontSize: 28, margin: 0 }}>Customer Hub</h1>
      <p className="muted" style={{ margin: "4px 0 20px" }}>A branded portal where each customer follows their fence job — timeline, photos, their property map, quote/invoice & messages.</p>

      <div className="card pad" style={{ marginBottom: 16 }}>
        <div className="row gap-8 kicker kicker-teal" style={{ marginBottom: 12 }}><Icon name="user" size={14} />Hub Access</div>
        <label className="toggle-row" onClick={() => store.setSettings({ hubDisabled: enabled })}>
          <span className={"sw" + (enabled ? " on" : "")}><span className="knob" /></span>
          <span style={{ fontSize: 13.5, fontWeight: 600 }}>Enable the Customer Hub</span>
        </label>
        <div className="pe-callout info" style={{ marginTop: 12 }}>
          <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>
            <b style={{ color: "var(--fg-2)" }}>Secure by design.</b> Each customer gets a unique, unguessable link (a 128-bit token). No login required for them, but the token can't be enumerated, and you can rotate or revoke it anytime. The link auto-attaches to quote & invoice emails.
          </p>
        </div>
      </div>

      {/* deposit policy + card surcharge both live under Settings → Money now;
          these pointers stay because the settings were born on this page. */}
      {(() => {
        const dep = (store.settings && store.settings.deposit) || {};
        const mode = dep.mode === "full_only" || dep.mode === "custom" ? dep.mode : "options";
        const opts = (Array.isArray(dep.options) ? dep.options.map(Number) : [25, 50]).filter(n => n >= 1 && n <= 99);
        const label = mode === "full_only" ? "Pay in full only" : mode === "custom" ? `${Number(dep.customPct) || 50}% deposit` : `Customer chooses · ${(opts.length ? opts : [25, 50]).join("/")}%`;
        return (
          <div className="card pad" style={{ marginBottom: 16 }}>
            <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center" }}>
              <span className="row gap-8 kicker" style={{ margin: 0 }}><Icon name="dollar" size={13} />Deposit policy</span>
              <span className="chip teal" style={{ fontSize: 10.5 }}>{label}</span>
              <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto" }} onClick={() => go("settings", { section: "deposit" })}>Manage in Settings → Money<Icon name="chevright" size={13} /></button>
            </div>
          </div>
        );
      })()}

      <div className="card pad" style={{ marginBottom: 16 }}>
        <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center" }}>
          <span className="row gap-8 kicker" style={{ margin: 0 }}><Icon name="dollar" size={13} />Card processing fee</span>
          {store.settings && store.settings.cardFee && store.settings.cardFee.enabled
            ? <span className="chip green" style={{ fontSize: 10.5 }}>{Math.max(1, Math.min(3, Number(store.settings.cardFee.pct) || 3))}% on card</span>
            : <span className="chip" style={{ fontSize: 10.5 }}>Off</span>}
          <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto" }} onClick={() => go("settings", { section: "tax" })}>Manage in Settings → Money<Icon name="chevright" size={13} /></button>
        </div>
      </div>

      <div className="card pad">
        <div className="row between" style={{ marginBottom: 12 }}>
          <span className="kicker">Customer Links</span>
          <span className="mono faint" style={{ fontSize: 11 }}>{customers.length} customers</span>
        </div>
        {customers.length === 0
          ? <EmptyState icon="user" title="No customers yet" sub="Links appear once you have customers — they're created automatically when you quote a job." />
          : <div style={{ display: "grid", gap: 8 }}>
              {customers.map(c => (
                <div key={c.id} className="hub-link-row">
                  <div className="cust-ava" style={{ width: 30, height: 30, fontSize: 13 }}>{(c.name || "?").charAt(0).toUpperCase()}</div>
                  <div className="hub-link-main" style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13.5 }}>{c.name || "Customer"}</div>
                    <div className="mono faint" style={{ fontSize: 11, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.hubToken ? shortLink(c.hubToken) : "No link yet"}</div>
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => openPreview(c)} title="Preview hub"><Icon name="map" size={13} /></button>
                  {c.hubToken && <button className="btn btn-ghost btn-sm" title="Rotate — revokes the current link" onClick={() => rotateLink(c)}><Icon name="undo" size={13} /></button>}
                  {c.email && <button className="btn btn-ghost btn-sm" title={"Email the link to " + c.email} onClick={() => emailLink(c)}><Icon name="mail" size={13} /></button>}
                  <button className="btn btn-outline btn-sm" onClick={() => copyLink(c)}>{copied === c.id ? <><Icon name="check" size={13} />Copied</> : <><Icon name="clipboard" size={13} />Copy link</>}</button>
                </div>
              ))}
            </div>}
      </div>
      {preview && (
        <Portal>
        <div className="hub-preview-overlay" data-dialog onClick={() => setPreview(null)}>
          <div className="hub-preview-frame" onClick={e => e.stopPropagation()}>
            <div className="hub-preview-bar">
              <span className="row gap-8" style={{ fontSize: 12.5, fontWeight: 600 }}><Icon name="map" size={13} className="muted" />Customer Hub preview</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setPreview(null)}><Icon name="x" size={14} />Close</button>
            </div>
            <div className="hub-preview-scroll">
              <PublicCustomerHub token={preview} state={store} />
            </div>
          </div>
        </div>
        </Portal>
      )}
    </div>
  );
}

Object.assign(window, { INTEGRATIONS, Integrations, CustomerHubSettings, genToken, hubUrl, useIntegrations });

export { useIntegrations, Integrations, genToken, hubUrl, CustomerHubSettings, INTEGRATIONS, OAUTH_PROVIDERS, SECRET_FIELDS };
